import clsx from 'clsx'
import dayjs from 'dayjs'
import { useCallback, useMemo, useState } from 'react'
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import { Link, useSearchParams } from 'react-router-dom'

import { Button } from '@cms/components/data/Button'
import { SystemMenu } from '@cms/components/data/Menu'
import { SystemPopover } from '@cms/components/data/Popover'
import { renderAttribute } from '@cms/components/renders'
import { Chip } from '@cms/components/ui/Chip'
import { ALLOWED_LIMITS, INPUT_VARIANTS, SUBTYPE_OPTIONS } from '@cms/utils/constants'
import { TEditorStoryVersion } from '@cms/utils/types'
import { groupBy, ISystemStory, shortenLabel, SUBTYPES, TDateFieldType, TEAMS, WORKFLOWS } from '@dl/declarations'
import MenuItem from '@mui/material/MenuItem'
import { DataPart } from '../../components/ui/DataPart'
import PageContainer from '../../components/ui/PageContainer'
import { generateSearchParam, getEditorStoryVersion, mapLookupsToOptions, renderDate, renderDateTime, renderSubtypeLabel } from '../../utils/helpers'
import { authorsLookupsQuery, subtypesLookupsQuery, templatesLookupsQuery, verticalsLookupsQuery } from '../../utils/queries'
import { renderRouteOptions, Routes, StoriesPath, TemplatesPath } from '../../utils/routes'

import { FaLock } from 'react-icons/fa'
import { IoIosArrowDown } from 'react-icons/io'

const FilterByDateMenu = () => {
    const [searchParams, setSearchParams] = useSearchParams()

    const startDate = searchParams.get(generateSearchParam(StoriesPath, 'startDate')) || 'N/A'
    const endDate = searchParams.get(generateSearchParam(StoriesPath, 'endDate')) || 'N/A'
    const dateType = searchParams.get(generateSearchParam(StoriesPath, 'dateType')) || 'publish_date'

    const [selectedDateType, setSelectedDateType] = useState<TDateFieldType>(dateType as TDateFieldType)
    const [selectedStartDate, setSelectedStartDate] = useState<Date | null>(null);
    const [selectedEndDate, setSelectedEndDate] = useState<Date | null>(null);

    const handleDateTypeChange = (type: TDateFieldType) => {
        setSelectedDateType(type)
    }

    const handleDateChange = (dates: [Date | null, Date | null]) => {
        const [start, end] = dates;
        setSelectedStartDate(start);
        setSelectedEndDate(end);
    };

    const handleApply = () => {
        setSearchParams((prevValue) => {
            prevValue.delete(generateSearchParam(StoriesPath, 'skip'))

            prevValue.set(generateSearchParam(StoriesPath, 'dateType'), selectedDateType)

            if (selectedStartDate) {
                prevValue.set(generateSearchParam(StoriesPath, 'startDate'), dayjs(selectedStartDate).format('YYYY-MM-DD'))
            } else {
                prevValue.delete(generateSearchParam(StoriesPath, 'startDate'))
            }

            if (selectedEndDate) {
                prevValue.set(generateSearchParam(StoriesPath, 'endDate'), dayjs(selectedEndDate).format('YYYY-MM-DD'))
            } else {
                prevValue.delete(generateSearchParam(StoriesPath, 'endDate'))
            }

            return prevValue
        })
    }

    const handleReset = () => {
        setSelectedStartDate(new Date())
        setSelectedEndDate(null)
        setSearchParams((prevValue) => {
            prevValue.delete(generateSearchParam(StoriesPath, 'startDate'))
            prevValue.delete(generateSearchParam(StoriesPath, 'endDate'))
            return prevValue
        })
    }

    const menuButtonLabel = useMemo(() => {
        if (startDate === 'N/A' && endDate === 'N/A') {
            return 'Date'
        }

        if (startDate !== 'N/A' && endDate === 'N/A') {
            return `From: ${renderDate(startDate)}`
        }

        if (startDate !== 'N/A' && endDate !== 'N/A') {
            return `From: ${renderDate(startDate)} To: ${renderDate(endDate)}`
        }

        return 'Date'
    }, [startDate, endDate])

    const isResetButtonEnabled = useMemo(() => {
        return !!(startDate !== 'N/A' || endDate !== 'N/A')
    }, [startDate, endDate])

    const isApplyButtonEnabled = useMemo(() => {
        return !!(selectedStartDate || selectedEndDate)
    }, [selectedStartDate, selectedEndDate])

    return (
        <SystemPopover
            trigger={{
                component: (
                    <Button
                        inputVariant='gray_text_white_bg'
                        rightIcon={(
                            <IoIosArrowDown
                                size={18}
                                className={clsx(
                                    'cursor-pointer fill-current',
                                )}
                            />
                        )}
                        className={clsx(
                            INPUT_VARIANTS['common'],
                            'font-normal!'
                        )}
                    >
                        {menuButtonLabel}
                    </Button>
                )
            }}
            content={{
                component: (
                    <div className='flex flex-col gap-4 p-4 shadow'>
                        <div className='flex justify-between items-center gap-2'>
                            <span className='whitespace-nowrap'>From:</span>
                            <Chip variant='common'>{selectedStartDate ? renderDate(selectedStartDate) : 'Anytime'}</Chip>
                            <span className='whitespace-nowrap'>To:</span>
                            <Chip variant='common'>{selectedEndDate ? renderDate(selectedEndDate) : 'Anytime'}</Chip>
                        </div>

                        <div className='flex items-center gap-2'>
                            {[
                                { label: 'Published', value: 'publish_date' },
                                { label: 'Updated', value: 'updated_at' },
                                { label: 'Created', value: 'created_at' },
                            ].map((option) => (
                                <Chip
                                    key={option.value}
                                    wrapperClassName='cursor-pointer'
                                    onClick={() => handleDateTypeChange(option.value as TDateFieldType)}
                                    variant={selectedDateType === option.value ? 'blue_text_white_bg' : 'common'}
                                >
                                    {option.label}
                                </Chip>
                            ))}
                        </div>

                        <div className='mx-auto'>
                            <DatePicker
                                selected={selectedStartDate}
                                onChange={handleDateChange}
                                startDate={selectedStartDate}
                                endDate={selectedEndDate}
                                maxDate={dayjs().add(1, 'day').toDate()}
                                selectsRange
                                inline
                            />
                        </div>

                        <div className='flex items-center gap-2 self-end'>
                            <Button
                                inputVariant='red_text_white_bg'
                                onClick={handleReset}
                                disabled={!isResetButtonEnabled}
                            >
                                Reset
                            </Button>
                            <Button
                                inputVariant='blue_text_white_bg'
                                onClick={handleApply}
                                disabled={!isApplyButtonEnabled}
                            >
                                Apply
                            </Button>
                        </div>
                    </div>
                )
            }}
        />
    )
}

const FilterByAuthorsMenu = () => {
    const [searchParams, setSearchParams] = useSearchParams()

    const { data: authors } = authorsLookupsQuery({
        // is_active: true,
        team: [TEAMS.NEWSROOM.SLUG, TEAMS.RESEARCH.SLUG].join(',')
    }, { fetchOnce: true })

    const handleSelectAuthor = (authorSlug: string) => {
        setSearchParams((prevValue) => {
            prevValue.delete(generateSearchParam(StoriesPath, 'skip'))

            if (isAuthorSelected(authorSlug)) {
                prevValue.delete(generateSearchParam(StoriesPath, 'authors'), authorSlug)
            }
            else {
                prevValue.append(generateSearchParam(StoriesPath, 'authors'), authorSlug)
            }

            return prevValue
        })
    }

    const isAuthorSelected = (authorSlug: string) => {
        return searchParams.has(generateSearchParam(StoriesPath, 'authors'), authorSlug)
    }

    return (
        <SystemPopover
            trigger={{
                component: (
                    <Button
                        inputVariant='gray_text_white_bg'
                        rightIcon={(
                            <IoIosArrowDown
                                size={18}
                                className={clsx(
                                    'cursor-pointer fill-current',
                                )}
                            />
                        )}
                        className={clsx(
                            INPUT_VARIANTS['common'],
                            'font-normal!'
                        )}
                    >
                        {searchParams.getAll(generateSearchParam(StoriesPath, 'authors')).length > 0 ? `Authors (${searchParams.getAll(generateSearchParam(StoriesPath, 'authors')).length})` : 'Authors'}
                    </Button>
                )
            }}
            content={{
                component: (
                    <div className='p-4 flex flex-col justify-center gap-1 min-w-80 overflow-y-auto shadow'>
                        {(authors || [])
                            .map((author) => (
                                <button
                                    key={author._id}
                                    type='button'
                                    onClick={() => handleSelectAuthor(author.slug)}
                                    className={clsx(
                                        'cursor-pointer text-start',
                                        isAuthorSelected(author.slug) && 'text-cs-600'
                                    )}
                                >
                                    {author.name}
                                </button>
                            ))}
                    </div>
                )
            }}
        />
    )
}

const FilterByVerticalsMenu = () => {
    const [searchParams, setSearchParams] = useSearchParams()

    const { data: subtypes } = subtypesLookupsQuery({
        used_for: 'stories',
        sort: 'order:asc',
    }, { fetchOnce: true })

    const { data: verticals } = verticalsLookupsQuery({
        subtype: ((subtypes || []).map((subtype) => subtype._id)).join(',')
    }, {
        enabled: (subtypes?.length || 0) > 0
    })

    const groupedVerticals = useMemo(() => {
        const mapped = mapLookupsToOptions(verticals || [], 'name', (item) => item.subtypeObject?.slug)
        return groupBy(mapped, item => item.groupKey)
    }, [verticals])

    const handleSelectVertical = (verticalSlug: string) => {
        setSearchParams((prevValue) => {
            prevValue.delete(generateSearchParam(StoriesPath, 'skip'))

            if (isVerticalSelected(verticalSlug)) {
                prevValue.delete(generateSearchParam(StoriesPath, 'verticals'), verticalSlug)
            }
            else {
                prevValue.append(generateSearchParam(StoriesPath, 'verticals'), verticalSlug)
            }

            return prevValue
        })
    }

    const handleSelectSubtype = (subtypeSlug: string) => {
        setSearchParams((prevValue) => {
            prevValue.delete(generateSearchParam(StoriesPath, 'skip'))
            prevValue.delete(generateSearchParam(StoriesPath, 'verticals'))

            prevValue.set(generateSearchParam(StoriesPath, 'subtypes'), subtypeSlug)

            return prevValue
        })
    }

    const isVerticalSelected = (verticalSlug: string) => {
        return searchParams.has(generateSearchParam(StoriesPath, 'verticals'), verticalSlug)
    }

    return (
        <SystemPopover
            trigger={{
                component: (
                    <Button
                        inputVariant='gray_text_white_bg'
                        rightIcon={(
                            <IoIosArrowDown
                                size={18}
                                className={clsx(
                                    'cursor-pointer fill-current',
                                )}
                            />
                        )}
                        className={clsx(
                            INPUT_VARIANTS['common'],
                            'font-normal!'
                        )}
                    >
                        {searchParams.getAll(generateSearchParam(StoriesPath, 'verticals')).length > 0 ? `Sections (${searchParams.getAll(generateSearchParam(StoriesPath, 'verticals')).length})` : 'Sections'}
                    </Button>
                )
            }}
            content={{
                component: (
                    <div className='grid grid-cols-2 justify-center p-4 gap-8 min-w-80 shadow'>
                        {[
                            SUBTYPES.NEWS,
                            SUBTYPES.RESEARCH
                        ]
                            .map((subtype) => (
                                <div className='flex flex-col gap-2 font-semibold' key={subtype.SLUG}>
                                    <button
                                        type='button'
                                        className='cursor-pointer text-cs-500 whitespace-nowrap text-start'
                                        onClick={() => handleSelectSubtype(subtype.SLUG)}
                                    >
                                        {subtype.TITLE}
                                    </button>

                                    {groupedVerticals[subtype.SLUG] && groupedVerticals[subtype.SLUG].map((option) => (
                                        <button
                                            key={option.slug}
                                            type='button'
                                            onClick={() => handleSelectVertical(option.slug)}
                                            className={clsx(
                                                'cursor-pointer text-start',
                                                isVerticalSelected(option.slug) && 'text-cs-600'
                                            )}
                                        >
                                            {option.label}
                                        </button>
                                    ))}
                                </div>
                            ))}
                    </div>
                )
            }}
        />
    )
}

const BASE_PATH = 'stories'

const StoriesPage = () => {
    const { data: templates = [] } = templatesLookupsQuery({ limit: "5" })

    const renderItem = useCallback((story: TEditorStoryVersion) => {
        return (
            <Link
                className={clsx(
                    'py-4 ps-4 pe-12 w-full',
                    'flex items-center justify-start gap-8',
                    'border border-cs-700 rounded-md',
                )}
                to={{
                    pathname: Routes.EDITOR,
                    search: `?${generateSearchParam(StoriesPath, "id", story._id)}`,
                }}
                key={story._id}
            >
                <div
                    className='bg-cs-200 text-cs-900 font-semibold p-1'
                >
                    {renderSubtypeLabel(story.subtypeObject!, "SHORT")}
                </div>

                <div className='w-full flex flex-col gap-4'>
                    <div className='grid grid-cols-5'>
                        <h4 className='col-span-4 text-cs-600 font-medium'>{story.headline}</h4>

                        {story.is_locked && (
                            <Chip
                                leftIcon={<FaLock size={16} />}
                                variant='white_text_dark_bg'
                                wrapperClassName='h-fit'
                            >
                                {shortenLabel(story?.lockedByObject?.name || 'Locked', 20)}
                            </Chip>
                        )}
                    </div>

                    <div className='grid grid-cols-3 xl:grid-cols-5 items-center justify-between gap-4'>
                        {renderAttribute({
                            title: { value: 'Status' },
                            value: { value: [story.story_workflow], className: 'text-cs-600' }
                        })}

                        {story.scheduleObject ? renderAttribute({
                            title: { value: `Scheduled for ${story.story_workflow}:` },
                            value: { value: [renderDateTime(story.scheduleObject.action_at)] }
                        })
                            : story.story_publish_date ? renderAttribute({
                                title: { value: 'Published:' },
                                value: { value: [renderDateTime(story.story_publish_date)] }
                            })
                                : renderAttribute({
                                    title: { value: 'Created:' },
                                    value: { value: [renderDateTime(story.story_created_at)] }
                                })}

                        {renderAttribute({
                            title: { value: 'Last modified:' },
                            value: { value: [renderDateTime(story.story_version_updated_at)] }
                        })}

                        {story.authorsObjects.length > 0 ? renderAttribute({
                            title: { value: 'Author:' },
                            value: { value: story.authorsObjects.map((author) => author.name) }
                        }) : (
                            <div />
                        )}

                        {story.verticalsObjects.length > 0 ? renderAttribute({
                            title: { value: 'Section:' },
                            value: { value: story.verticalsObjects.map((vertical) => vertical.name) }
                        }) : (
                            <div />
                        )}
                    </div>
                </div>
            </Link>
        )
    }, [])

    return (
        <PageContainer
            title={renderRouteOptions(Routes.STORIES).title}
        >
            <DataPart<ISystemStory>
                tableSize='full'

                pageSize={ALLOWED_LIMITS["15"]}

                distribution={{
                    type: 'grid',
                    renderItem: renderItem,
                    noOfColumns: "1"
                }}

                queryOptions={{
                    gcTime: 0,
                    staleTime: 0,
                    basePath: BASE_PATH,
                    transformData: (data) => {
                        return {
                            ...data,
                            data: data.data.map(getEditorStoryVersion)
                        }
                    },
                }}

                defaultSearchParams={{
                    [generateSearchParam(BASE_PATH, 'subtypes')]: SUBTYPES.NEWS.SLUG
                }}


                createOptions={
                    templates.length === 0 ? {
                        allow: true,
                        path: {
                            pathname: Routes.EDITOR,
                        }
                    } : {
                        allow: true,
                        component: (
                            <SystemMenu
                                trigger={{
                                    component: (
                                        <Button
                                            inputVariant='white_text_blue_bg'
                                            rightIcon={
                                                <IoIosArrowDown size={18} />
                                            }
                                            className={clsx(
                                                INPUT_VARIANTS['common']
                                            )}
                                        >
                                            Create
                                        </Button>
                                    )
                                }}
                                content={{
                                    component: (
                                        <div className='p-2 flex flex-col gap-y-1 rounded shadow border'>
                                            {templates.map((item) => (
                                                <Link
                                                    key={item._id}
                                                    to={{
                                                        pathname: Routes.EDITOR,
                                                        search: `?${generateSearchParam(TemplatesPath, "id", item._id)}`,
                                                    }}
                                                >
                                                    <MenuItem
                                                    >
                                                        {item.info.name}
                                                    </MenuItem>
                                                </Link>
                                            ))}

                                            <MenuItem
                                                className='self-center'
                                            >
                                                <Button
                                                    as='link'
                                                    to={{
                                                        pathname: Routes.TEMPLATES
                                                    }}
                                                    inputVariant='none'
                                                >
                                                    Browse all templates
                                                </Button>
                                            </MenuItem>
                                        </div>
                                    )
                                }}
                            />
                        )
                    }
                }

                searchOptions={{
                    allow: true
                }}

                filters={{
                    startDate: {
                        type: 'none',
                        label: 'Start Date',
                    },
                    endDate: {
                        type: 'none',
                        label: 'End Date',
                    },
                    dateType: {
                        type: 'none',
                        label: 'Date Type',
                    },
                    date: {
                        type: 'custom',
                        label: 'Filter by date',
                        component: <FilterByDateMenu />
                    },
                    subtypes: {
                        type: 'select',
                        label: 'Subtypes',
                        options: SUBTYPE_OPTIONS,
                        allowMultiSelect: true,
                        hide: true
                    },
                    authors: {
                        type: 'custom',
                        label: 'Authors',
                        component: <FilterByAuthorsMenu />
                    },
                    verticals: {
                        type: 'custom',
                        label: 'Filter by section',
                        component: <FilterByVerticalsMenu />
                    },
                    tags: {
                        type: 'none',
                        label: 'Tags',
                    },
                    workflows: {
                        type: 'checkbox',
                        label: 'Only Published',
                        onValue: WORKFLOWS.Published,
                        offValue: "all",
                        defaultValue: "all",
                    }
                }}

                subtypes={
                    [
                        SUBTYPES.NEWS,
                        SUBTYPES.RESEARCH,
                    ]}
            />
        </PageContainer>
    )
}

export default StoriesPage